import React, { FC, useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import MoveIcon from "../../../assets/icons/arrow-move.svg?react";
import SettingsIcon from "../../../assets/icons/cog.svg?react";
import DiscordIcon from "../../../assets/icons/discord.svg?react";
import EraserIcon from "../../../assets/icons/eraser.svg?react";
import OpenIcon from "../../../assets/icons/files-history.svg?react";
import HelpIcon from "../../../assets/icons/help.svg?react";

import PanIcon from "../../../assets/icons/pan.svg?react";
import { platformState, toolState, uiState } from "../states/uiStates";
import { MapTool } from "../types/tools";
import { AuthButton } from "./AuthButton/AuthButton";
import { DragonToolButton } from "./DragonButton/DragonToolButton";
import { NewToolbarButton } from "./NewToolbarButton";
import { ShapeToolButton } from "./ShapePopup/ShapeToolButton";
import { CollaborateToolbarButton } from "@shared/Toolbar/CollaboratePopup/CollaborateToolbarButton";
import { StrategiesButton } from "./StrategiesButton/StrategiesButton";
import { ToolButton } from "./ToolButton";
import { WardToolButton } from "./WardButton/WardToolButton";
import { SaveImageToolButton } from "@shared/Toolbar/SaveImageToolButton";
import { TextButton } from "@shared/Toolbar/TextButton/TextButton";
import { PencilButton } from "@shared/Toolbar/PencilButton/PencilButton";
import { BASE_COLORS } from "@shared/Toolbar/ToolColorPicker";

interface ToolbarProps {}

export const Toolbar: FC<ToolbarProps> = ({}) => {
	const toolSnap = useSnapshot(toolState);
	const [lastTool, setLastTool] = useState<MapTool | null>();
	const platformSnap = useSnapshot(platformState);

	useEffect(() => {
		const downListener = (e: KeyboardEvent) => {
			const focusedElement = document.activeElement?.localName;
			if (focusedElement && ["textarea", "input"].includes(focusedElement)) {
				return;
			}
			if (e.key === "w") {
				toolState.selectedTool = MapTool.WARD;
			}
			if (e.key === "e") {
				toolState.selectedTool = MapTool.ERASER;
			}
			if (e.key === "l") {
				toolState.selectedTool = MapTool.PENCIL;
				toolState.properties[MapTool.PENCIL].isArrow = false;
			}
			if (e.key === "a") {
				toolState.selectedTool = MapTool.PENCIL;
				toolState.properties[MapTool.PENCIL].isArrow = true;
			}
			if (e.key === "s") {
				toolState.selectedTool = MapTool.SHAPE;
			}
			if (e.key === "t") {
				toolState.selectedTool = MapTool.TEXT;
			}
			const toolsWithColor: [MapTool.PENCIL, MapTool.TEXT, MapTool.SHAPE, MapTool.WARD] = [
				MapTool.PENCIL,
				MapTool.TEXT,
				MapTool.SHAPE,
				MapTool.WARD,
			];
			const foundTool = toolsWithColor.find((tool) => tool == toolState.selectedTool);
			if (e.key == "r" && foundTool) {
				if (foundTool == MapTool.WARD) {
					toolState.properties[foundTool].areaColor = "red";
				} else {
					toolState.properties[foundTool].color = BASE_COLORS[3];
				}
			}
			if (e.key == "b" && foundTool) {
				if (foundTool == MapTool.WARD) {
					toolState.properties[foundTool].areaColor = "blue";
				} else {
					toolState.properties[foundTool].color = BASE_COLORS[2];
				}
			}
			if (e.key === "Control" && !!toolSnap.selectedTool && !lastTool) {
				setLastTool(toolSnap.selectedTool);
				toolState.selectedTool = MapTool.MOVE;
			}
			if (e.key === " " && !!toolSnap.selectedTool && !lastTool) {
				setLastTool(toolSnap.selectedTool);
				toolState.selectedTool = MapTool.PAN;
			}
		};

		const upListener = (e: KeyboardEvent) => {
			if (e.key === "Control" && lastTool) {
				toolState.selectedTool = lastTool;
				setLastTool(null);
			}
			if (e.key === " " && lastTool) {
				toolState.selectedTool = lastTool;
				setLastTool(null);
			}
		};

		window.addEventListener("keydown", downListener);
		window.addEventListener("keyup", upListener);

		return () => {
			window.removeEventListener("keydown", downListener);
			window.removeEventListener("keydown", upListener);
		};
	}, [toolSnap.selectedTool, lastTool]);

	return (
		<div className="bg-base-200 mr-2 w-12 border-right flex flex-col justify-between p-1 border-r border-border-base-200">
			<div className={"flex flex-col gap-2"}>
				<ToolButton
					tool={MapTool.PAN}
					tooltipMessage={
						<span>
							Pan around the map
							<br />
							[Space] or [Middle Mouse Button]
						</span>
					}
				>
					<PanIcon width={32} height={32} />
				</ToolButton>
				<ToolButton tool={MapTool.MOVE} tooltipMessage={"Move objects on the map [Ctrl]"}>
					<MoveIcon width={32} height={32} />
				</ToolButton>
				<PencilButton />
				<TextButton />
				<ShapeToolButton />
				<WardToolButton />
				<ToolButton tool={MapTool.ERASER} tooltipMessage={"Eraser [e]"}>
					<EraserIcon width={32} height={32} />
				</ToolButton>
				<DragonToolButton />
				<div className={"bg-slate-500"} style={{ height: 1 }}></div>
				{platformSnap.platform !== "desktop" && <StrategiesButton />}
				<CollaborateToolbarButton />
				<NewToolbarButton
					onClick={() => (uiState.modal = "import")}
					tooltipMessage={"Import game from history"}
					testId="toolbar-btn-import"
				>
					<OpenIcon width={32} height={32} />
				</NewToolbarButton>
				<SaveImageToolButton></SaveImageToolButton>
			</div>
			<div className={"flex flex-col gap-2"}>
				{platformSnap.platform !== "desktop" && <AuthButton />}
				{/*{IS_DEV && <ImportStratButton />}*/}

				<NewToolbarButton
					onClick={() => (uiState.modal = "settings")}
					tooltipMessage={"Settings"}
					testId="toolbar-btn-settings"
				>
					<SettingsIcon width={32} height={32} />
				</NewToolbarButton>
				<NewToolbarButton
					onClick={() =>
						window.open(
							"https://link.duiker101.net/visit/5d0cd7f1-9b79-47ed-b15f-780856c26845",
						)
					}
					tooltipMessage={"Join our Discord Server"}
					testId="toolbar-btn-discord"
				>
					<DiscordIcon width={32} height={32} />
				</NewToolbarButton>
				<NewToolbarButton
					onClick={() => (uiState.modal = "help")}
					tooltipMessage={"Help"}
					testId="toolbar-btn-help"
				>
					<HelpIcon width={32} height={32} />
				</NewToolbarButton>
			</div>
		</div>
	);
};
